import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";

import { Loading, Refusal } from "@lopie/kernel-ui";

import Answer from "../components/Answer.jsx";
import AskBox from "../components/AskBox.jsx";
import FilterRow from "../components/FilterRow.jsx";
import PlotBar from "../components/PlotBar.jsx";
import { failureText } from "../lib/reports.js";
import { isBadQuestion } from "../lib/run.js";
import {
  BUCKET_LABEL,
  FUNCTION_LABEL,
  MAX_DIMENSIONS,
  buildSpec,
  defaultOp,
  describe,
  describeLink,
  dimensionFields,
  exposureOf,
  fieldsOf,
  filterFields,
  humanise,
  kindOf,
  measureFunctions,
  opsFor,
  problems as problemsOf,
  typesOf,
  withCurrencySplit,
  newState,
  suggestGrouping,
} from "../lib/spec.js";

const MAX_MEASURES = 4;

/** A field, as a person reads it, with what it can and cannot be used for. */
function fieldLabel(field) {
  const marks = [];
  if (exposureOf(field) === "aggregate_only") marks.push("counts only");
  if (field.minor_units) marks.push("money");
  return `${humanise(field.name)}${marks.length ? ` (${marks.join(", ")})` : ""}`;
}

const update = (list, index, patch) => list.map((x, i) => (i === index ? { ...x, ...patch } : x));
const without = (list, index) => list.filter((_, i) => i !== index);

function Section({ title, hint, children }) {
  return (
    <Stack sx={{ gap: 1 }}>
      <Typography variant="subtitle2" component="h3">{title}</Typography>
      {hint ? <Typography variant="caption" color="text.secondary">{hint}</Typography> : null}
      {children}
    </Stack>
  );
}

function MeasureRow({ m, fields, onChange, onRemove, removable }) {
  const field = fields.find((f) => f.name === m.field);
  const allowed = measureFunctions(field);
  return (
    <Stack direction="row" sx={{ gap: 1, flexWrap: "wrap", alignItems: "center" }}>
      <TextField
        select size="small" label="Show" value={allowed.includes(m.fn) ? m.fn : allowed[0]}
        onChange={(e) => onChange({ fn: e.target.value })} sx={{ minWidth: 190 }}
      >
        {allowed.map((fn) => <MenuItem key={fn} value={fn}>{FUNCTION_LABEL[fn]}</MenuItem>)}
      </TextField>
      <TextField
        select size="small" label="Of" value={m.field}
        slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
        onChange={(e) => {
          const next = fields.find((f) => f.name === e.target.value);
          const ok = measureFunctions(next);
          onChange({ field: e.target.value, fn: ok.includes(m.fn) ? m.fn : ok[0] });
        }}
        sx={{ minWidth: 240 }}
      >
        <MenuItem value="">Records</MenuItem>
        {fields.map((f) => <MenuItem key={f.name} value={f.name}>{fieldLabel(f)}</MenuItem>)}
      </TextField>
      {removable ? (
        <IconButton aria-label="Remove this measure" size="small" onClick={onRemove}>×</IconButton>
      ) : null}
    </Stack>
  );
}

function DimensionRow({ d, fields, onChange, onRemove }) {
  const field = fields.find((f) => f.name === d.field);
  const time = field && kindOf(field) === "time";
  return (
    <Stack direction="row" sx={{ gap: 1, flexWrap: "wrap", alignItems: "center" }}>
      <TextField
        select size="small" label="Group by" value={d.field}
        onChange={(e) => {
          const next = fields.find((f) => f.name === e.target.value);
          onChange({ field: e.target.value, bucket: next && kindOf(next) === "time" ? (d.bucket || "month") : "" });
        }}
        sx={{ minWidth: 240 }}
      >
        {fields.map((f) => <MenuItem key={f.name} value={f.name}>{humanise(f.name)}</MenuItem>)}
      </TextField>
      {time ? (
        <TextField
          select size="small" label="By" value={d.bucket || "month"}
          onChange={(e) => onChange({ bucket: e.target.value })} sx={{ minWidth: 130 }}
        >
          {Object.entries(BUCKET_LABEL).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
        </TextField>
      ) : null}
      <IconButton aria-label="Remove this grouping" size="small" onClick={onRemove}>×</IconButton>
    </Stack>
  );
}

export default function Build({ schema, state, setState, query, onAnswer, runNonce, onSave, onUpdate, editing, onDrill, onAsk, sources = [] }) {
  const [out, setOut] = useState(null);
  const [running, setRunning] = useState(false);
  const [name, setName] = useState("");
  const [saved, setSaved] = useState("");

  const fields = useMemo(() => fieldsOf(schema, state.type), [schema, state.type]);
  const dims = useMemo(() => dimensionFields(fields), [fields]);
  const filterable = useMemo(() => filterFields(fields), [fields]);
  const issues = useMemo(() => problemsOf(state, schema), [state, schema]);

  const run = useCallback(async () => {
    if (problemsOf(state, schema).length) return;
    setRunning(true);
    const answer = await query(buildSpec(state, schema));
    setOut({ ...answer, state });
    setRunning(false);
    if (answer.ok) onAnswer?.(answer.result);
  }, [state, schema, query, onAnswer]);

  useEffect(() => {
    if (runNonce > 0) run();
    // Only when asked: a form that re-ran on every keystroke would be a
    // request per character against a service that counts records.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runNonce]);

  const set = (patch) => { setState({ ...state, ...patch }); setSaved(""); };

  // One figure has nothing to draw. Asking for bars or a line of it adds the grouping that gives it something, and
  // runs it, so the click does what it says; the sentence under the control says what was added and how to undo it.
  const suggested = useMemo(() => suggestGrouping(fields, state.chart === "line" ? "line" : "bar"), [fields, state.chart]);
  const [pendingRun, setPendingRun] = useState(false);
  const [grouped, setGrouped] = useState(null);
  const chooseChart = (v) => {
    const wantsDrawing = v === "bar" || v === "line";
    const want = suggestGrouping(fields, v);
    if (wantsDrawing && !state.dimensions.some((d) => d.field) && want) {
      setState({ ...state, chart: v, dimensions: [{ field: want.field, bucket: want.bucket }] });
      setGrouped({ field: want.field, text: `${humanise(want.field)}${want.bucket ? " by month" : ""}` });
      setSaved("");
      setPendingRun(true);
    } else {
      set({ chart: v });
    }
  };
  useEffect(() => {
    if (pendingRun) { setPendingRun(false); run(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingRun]);

  return (
    <Stack sx={{ gap: 3 }}>
      {onAsk ? <AskBox onAsk={onAsk} /> : null}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack sx={{ gap: 2 }}>
          <Section title="What to look at">
            <TextField
              select size="small" label="Records of type" value={state.type}
              onChange={(e) => { setState(newState(e.target.value)); setOut(null); }}
              sx={{ maxWidth: 320 }}
            >
              {typesOf(schema).map((t) => <MenuItem key={t.type} value={t.type}>{t.type}</MenuItem>)}
            </TextField>
          </Section>

          <Section title="What to show">
            {state.measures.map((m, i) => (
              <MeasureRow
                key={i} m={m} fields={fields} removable={state.measures.length > 1}
                onChange={(patch) => set({ measures: update(state.measures, i, patch) })}
                onRemove={() => set({ measures: without(state.measures, i) })}
              />
            ))}
            {state.measures.length < MAX_MEASURES ? (
              <Button size="small" sx={{ alignSelf: "flex-start" }}
                onClick={() => set({ measures: [...state.measures, { fn: "count", field: "" }] })}>
                Add another
              </Button>
            ) : null}
          </Section>

          <Section
            title="Break it down"
            hint="A field that identifies a person or a record is not offered here: one bar per name would be a list."
          >
            {state.dimensions.map((d, i) => (
              <DimensionRow
                key={i} d={d} fields={dims}
                onChange={(patch) => set({ dimensions: update(state.dimensions, i, patch) })}
                onRemove={() => set({ dimensions: without(state.dimensions, i) })}
              />
            ))}
            {state.dimensions.length < MAX_DIMENSIONS && dims.length ? (
              <Button size="small" sx={{ alignSelf: "flex-start" }}
                onClick={() => set({ dimensions: [...state.dimensions, { field: dims[0].name, bucket: kindOf(dims[0]) === "time" ? "month" : "" }] })}>
                {state.dimensions.length ? "Add a second grouping" : "Group by something"}
              </Button>
            ) : null}
          </Section>

          <Section title="Only where">
            {state.filters.map((f, i) => (
              <FilterRow
                key={i} f={f} fields={filterable}
                onChange={(patch) => set({ filters: update(state.filters, i, patch) })}
                onRemove={() => set({ filters: without(state.filters, i) })}
              />
            ))}
            {filterable.length ? (
              <Button size="small" sx={{ alignSelf: "flex-start" }}
                onClick={() => set({ filters: [...state.filters, { field: filterable[0].name, op: defaultOp(filterable[0]), value: "" }] })}>
                Add a filter
              </Button>
            ) : null}
          </Section>

          {sources.length > 1 ? (
            <Section
              title="Held by"
              hint="Limit this to records one connector wrote, and to those another connector also holds through an accepted link. A record with no such link you may open is left out, and the answer says how many."
            >
              <Stack direction="row" sx={{ gap: 1, flexWrap: "wrap" }}>
                <TextField
                  select size="small" label="Written by" value={state.source || ""} sx={{ minWidth: 200 }}
                  slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
                  onChange={(e) => {
                    const { source, ...rest } = state;
                    setState(e.target.value ? { ...rest, source: e.target.value } : rest); setSaved("");
                  }}
                >
                  <MenuItem value="">Any connector</MenuItem>
                  {sources.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </TextField>
                <TextField
                  select size="small" label="Also held by" value={state.linked?.source || ""} sx={{ minWidth: 200 }}
                  slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
                  onChange={(e) => {
                    const { linked, ...rest } = state;
                    setState(e.target.value ? { ...rest, linked: { source: e.target.value } } : rest); setSaved("");
                  }}
                >
                  <MenuItem value="">No other connector</MenuItem>
                  {sources.filter((s) => s !== state.source).map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </TextField>
              </Stack>
            </Section>
          ) : null}

          <Section title="Show it as">
            <ToggleButtonGroup
              exclusive size="small" value={state.chart} aria-label="Chart type"
              onChange={(_, v) => v && chooseChart(v)}
            >
              <ToggleButton value="auto">Best fit</ToggleButton>
              <ToggleButton value="bar">Bars</ToggleButton>
              <ToggleButton value="line">Line</ToggleButton>
              <ToggleButton value="table">Table</ToggleButton>
            </ToggleButtonGroup>
          </Section>

          {state.source || state.linked ? (
            <Alert
              severity="info" variant="outlined"
              action={<Button color="inherit" size="small" onClick={() => { const { source, linked, ...rest } = state; setState(rest); setSaved(""); }}>Take this off</Button>}
            >
              Only records {describeLink(state)}. A record with no such link you may open is left out, and the answer says how many.
            </Alert>
          ) : null}

          {!state.dimensions.some((d) => d.field) && state.chart !== "table" ? (
            <Alert
              severity="info" variant="outlined" role="note"
              action={suggested ? (
                <Button color="inherit" size="small"
                  onClick={() => { setState({ ...state, dimensions: [{ field: suggested.field, bucket: suggested.bucket }] }); setGrouped(null); setSaved(""); setPendingRun(true); }}>
                  Group by {humanise(suggested.field).toLowerCase()}
                </Button>
              ) : null}
            >
              {suggested
                ? "This is one figure, so there is nothing to draw. Break it down by something and it becomes a chart."
                : "This kind of record has nothing to break a count down by, so it is one figure."}
            </Alert>
          ) : null}
          {grouped && state.dimensions.length === 1 && state.dimensions[0].field === grouped.field ? (
            <Alert severity="info" variant="outlined" role="status"
              action={<Button color="inherit" size="small" onClick={() => { setState({ ...state, dimensions: [] }); setGrouped(null); }}>Take it off</Button>}>
              Grouped by {grouped.text.toLowerCase()} so there is something to draw.
            </Alert>
          ) : null}

          {issues.map((p) => (
            <Alert
              key={p.code + p.text} severity="warning" variant="outlined"
              action={p.fix ? (
                <Button color="inherit" size="small" onClick={() => setState(withCurrencySplit(state, p.fix.field))}>
                  {p.fix.label}
                </Button>
              ) : null}
            >
              {p.text}
            </Alert>
          ))}

          <Typography variant="body2" color="text.secondary">{describe(state, schema)}</Typography>

          <Stack direction="row" sx={{ gap: 1, flexWrap: "wrap", alignItems: "center" }}>
            <Button variant="contained" onClick={run} disabled={running || issues.length > 0}>
              {running ? "Running…" : "Run"}
            </Button>
            <TextField
              size="small" label={editing ? "Save as a new report" : "Name this report"} value={name}
              onChange={(e) => setName(e.target.value)} sx={{ minWidth: 220 }}
            />
            {editing ? (
              <Button
                variant="outlined" disabled={issues.length > 0}
                onClick={async () => {
                  try {
                    const done = await onUpdate(editing, state);
                    setSaved(`Updated “${editing.name}”. It is now version ${done}; the one before it is kept under Versions.`);
                  } catch (e) {
                    setSaved(failureText(e));
                  }
                }}
              >
                Update “{editing.name}”
              </Button>
            ) : null}
            <Button
              disabled={!name.trim() || issues.length > 0}
              onClick={async () => {
                const label = name.trim();
                try {
                  await onSave(label, state);
                  setSaved(`Saved “${label}”. It is yours alone until you share it from the Saved tab.`);
                  setName("");
                } catch (e) {
                  setSaved(failureText(e));
                }
              }}
            >
              Save
            </Button>
            {saved ? <Typography variant="caption" role="status" color="text.secondary">{saved}</Typography> : null}
          </Stack>
        </Stack>
      </Paper>

      {out?.ok ? (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <PlotBar
            fields={dims} dimensions={state.dimensions}
            onChange={(dimensions) => { setState({ ...state, dimensions }); setGrouped(null); setSaved(""); setPendingRun(true); }}
          />
        </Paper>
      ) : null}

      {running ? <Loading label="Asking the kernel" minHeight={120} /> : null}
      {!running && out ? (
        out.ok ? (
          <Answer result={out.result} state={out.state} schema={schema} onDrill={onDrill} />
        ) : isBadQuestion(out.error) ? (
          <Alert severity="warning">
            <Typography variant="body2" sx={{ fontWeight: 600 }}>The kernel did not accept that question.</Typography>
            <Typography variant="body2">{out.error.message}</Typography>
          </Alert>
        ) : (
          <Refusal failure={out.error} actor="Insights" onRetry={run} />
        )
      ) : null}
    </Stack>
  );
}
